import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAccount } from '../api/api';
import { ArrowLeft, Eye, EyeOff, Lock, Mail, User, UserPlus } from 'lucide-react';

const Logo = ({ size = 32 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 7.5L12 4L20 7.5L12 11L4 7.5Z" fill="currentColor" />
    <path d="M4 11.5L12 15L20 11.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 15.5L12 19L20 15.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const AccountCreate = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (password !== passwordConfirmation) {
      setError('As senhas não coincidem.');
      setLoading(false);
      return;
    }

    if (!termsAccepted) {
      setError('Você precisa aceitar os termos e condições para criar uma conta.');
      setLoading(false);
      return;
    }

    try {
      const response = await createAccount(name, email, password, passwordConfirmation);

      if (response) {
        setSuccess("Conta criada com sucesso! Verifique seu email para ativar sua conta. Um link de ativação foi enviado para o endereço informado.");
        setName('');
        setEmail('');
        setPassword('');
        setPasswordConfirmation('');
      } else {
        setError("Erro ao criar conta, tente novamente.");
      }
    } catch (error) {
      setError("Erro ao criar conta, tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex flex-col items-center justify-center p-4 bg-grid">
      <div className="absolute top-8 left-8">
        <Link
          to="/"
          className="flex items-center gap-2 text-gray-500 hover:text-purple-600 font-bold transition-all group"
        >
          <div className="w-10 h-10 rounded-full bg-white border border-gray-100 flex items-center justify-center group-hover:bg-purple-50 group-hover:border-purple-100 shadow-sm transition-all">
            <ArrowLeft size={18} />
          </div>
          <span>Voltar ao início</span>
        </Link>
      </div>

      <div className="w-full max-w-md animate-fade-in">
        <div className="flex flex-col items-center text-center mb-10">
          <div className="flex items-center text-black mb-4">
            <Logo size={48} />
            <span className="font-display text-4xl ml-4 tracking-tighter">SORA</span>
          </div>
          <h1 className="text-3xl font-black mb-2 font-hero">
            Crie sua conta no Sora
          </h1>
          <p className="text-gray-500 font-medium">
            Comece a organizar suas aulas e economizar tempo hoje mesmo.
          </p>
        </div>

        <div className="bg-white p-8 md:p-10 rounded-[3rem] shadow-xl border border-gray-100">
          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-xl">
              <p className="text-green-800 font-medium text-sm">{success}</p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-xl">
              <p className="text-red-800 font-medium text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Seu nome
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <User size={18} />
                </div>
                <input
                  type="text"
                  placeholder="Seu nome completo"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Seu e-mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Mail size={18} />
                </div>
                <input
                  type="email"
                  placeholder="exemplo@escola.com"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Sua senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-12 outline-none transition-all font-medium"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-black uppercase tracking-widest text-gray-400 mb-2 ml-1">
                Confirme sua senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <Lock size={18} />
                </div>
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  required
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  className="w-full bg-gray-50 border-2 border-gray-100 focus:border-purple-200 rounded-2xl py-4 pl-12 pr-4 outline-none transition-all font-medium"
                />
              </div>
            </div>

            <div className="flex items-start gap-3 pt-2">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="w-5 h-5 border-2 border-gray-300 rounded focus:ring-purple-500 text-purple-600 transition-colors cursor-pointer accent-purple-600"
                />
              </div>
              <label htmlFor="terms" className="text-sm text-gray-500 font-medium cursor-pointer select-none">
                Eu concordo com os <Link to="/terms-conditions" target="_blank" className="text-purple-600 font-bold hover:underline">Termos e Condições</Link> e com a Política de Privacidade do Sora.
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-black text-white font-display py-5 rounded-2xl text-lg shadow-xl shadow-gray-200 transition-all active:scale-95 flex items-center justify-center gap-3 ${loading ? 'opacity-70 cursor-wait' : 'hover:bg-gray-800'}`}
            >
              {loading ? (
                <div className="w-6 h-6 border-4 border-white/20 border-t-white rounded-full animate-spin"></div>
              ) : (
                <>
                  <UserPlus size={20} />
                  Criar minha conta
                </>
              )}
            </button>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-500 font-medium">
              Já tem uma conta? <br className="sm:hidden" />
              <Link
                to="/login"
                className="text-purple-600 font-bold hover:underline ml-1"
              >
                Entre agora
              </Link>
            </p>
          </div>
        </div>
      </div>

      <p className="mt-12 text-gray-400 text-sm font-medium">
        © 2025 Sôra — Todos os direitos reservados.
      </p>
    </div>
  );
};

export default AccountCreate;
